import { useQuery } from '@tanstack/react-query';
import { useSession } from 'next-auth/react';
import { apiClient } from '@/services/api-client';
import { shouldUseMockData } from '@/lib/mock-data/utils';

export interface ResourceUsage {
  used: number;
  limit: number;
  percentage: number;
}

export interface PodGroupUsage {
  pod_id: number;
  name: string;
  groups: ResourceUsage;
}

export interface UsageResponse {
  tier: string;
  accounts: ResourceUsage;
  groups: ResourceUsage;
  ai_chat: ResourceUsage;
  profiles: ResourceUsage;
  /** SIG-799: Operator-desk CLIENT seats (max_managed_clients). Optional — older payloads omit it. */
  managed_clients?: ResourceUsage;
  browser_automation: ResourceUsage;
  /**
   * SIG-1677: only present while PODS_ENABLED is on — absence (dark flag) and
   * `limit === 0` (non-Fund tier) are BOTH "no pods" reads for any consumer
   * deciding whether to show a pods surface.
   */
  pods?: ResourceUsage;
  pod_groups?: PodGroupUsage[];
}

export const usageQueryKeys = {
  summary: () => ["usage-data"] as const,
};

export interface DailyUsage {
  date: string;
  ai_chat: number;
}

function getMockUsageData(): UsageResponse {
  return {
    tier: 'subscription',
    accounts: { used: 3, limit: 5, percentage: 60 },
    groups: { used: 2, limit: 5, percentage: 40 },
    ai_chat: { used: 160, limit: 200, percentage: 80 },
    profiles: { used: 1, limit: 1, percentage: 100 },
    browser_automation: { used: 0, limit: -1, percentage: 0 },
  };
}

export const usageService = {
  async getUsageSummary(): Promise<UsageResponse> {
    if (shouldUseMockData()) {
      return getMockUsageData();
    }
    const response = await apiClient.get<UsageResponse>('/usage/summary');
    return response.data;
  },

  useUsageData(options?: { enabled?: boolean }) {
    const { data: session } = useSession();
    const isMockMode = shouldUseMockData();

    return useQuery<UsageResponse>({
      queryKey: usageQueryKeys.summary(),
      queryFn: async () => {
        if (isMockMode) {
          return getMockUsageData();
        }

        const response = await apiClient.get<UsageResponse>('/usage/summary');
        return response.data;
      },
      // ARCH S2 (D3): closed panels pass `enabled: isOpen` — the ⌘P pod row
      // reads this while the palette is open.
      enabled: (options?.enabled ?? true) && (isMockMode || !!session?.accessToken),
      staleTime: 60_000,
      refetchOnWindowFocus: true,
    });
  },
};
